import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  Renderer2
} from '@angular/core';

@Directive({
  selector: '[libSwipeReply]',
  standalone: true
})
export class SwipeReplyDirective implements OnDestroy {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);

  readonly libSwipeReplyDisabled: InputSignal<boolean> = input<boolean>(false);
  readonly libSwipeReplyDirection: InputSignal<'left' | 'right'> = input<'left' | 'right'>('right');
  readonly swipeReply: OutputEmitterRef<void> = output<void>();

  private swipeStartX: number = 0;
  private swipeStartY: number = 0;
  private swipeDirection: 'horizontal' | 'vertical' | null = null;
  private swipeOffset: number = 0;
  private readonly swipeThreshold: number = 60;
  private readonly swipeMax: number = 80;
  private hintElement: HTMLElement | null = null;
  private snappingTimer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    this.swipeStartX = event.touches[0].clientX;
    this.swipeStartY = event.touches[0].clientY;
    this.swipeDirection = null;
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (this.libSwipeReplyDisabled()) return;

    const deltaX: number = event.touches[0].clientX - this.swipeStartX;
    const deltaY: number = event.touches[0].clientY - this.swipeStartY;

    if (!this.swipeDirection) {
      if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
        this.swipeDirection = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      }
      return;
    }

    if (this.swipeDirection === 'horizontal') {
      const isLeft: boolean = this.libSwipeReplyDirection() === 'left';
      const raw: number = isLeft ? -deltaX : deltaX;
      this.swipeOffset = Math.max(0, Math.min(raw, this.swipeMax));
      this.applyTransform();
      this.updateHint();
    }
  }

  @HostListener('touchend')
  onTouchEnd(): void {
    if (this.swipeDirection === 'horizontal' && this.swipeOffset >= this.swipeThreshold) {
      this.swipeReply.emit();
    }

    if (this.swipeOffset > 0) {
      this.snapBack();
    }

    this.swipeDirection = null;
  }

  ngOnDestroy(): void {
    this.removeHint();
    if (this.snappingTimer) {
      clearTimeout(this.snappingTimer);
    }
  }

  private applyTransform(): void {
    const el: HTMLElement = this.elementRef.nativeElement;
    const isLeft: boolean = this.libSwipeReplyDirection() === 'left';
    const translate: number = isLeft ? -this.swipeOffset : this.swipeOffset;
    this.renderer.setStyle(el, 'transform', `translateX(${translate}px)`);
    this.renderer.setStyle(el, 'transition', 'none');
  }

  private snapBack(): void {
    const el: HTMLElement = this.elementRef.nativeElement;
    this.renderer.setStyle(el, 'transition', 'transform 0.2s ease-out');
    this.renderer.setStyle(el, 'transform', '');
    this.swipeOffset = 0;
    this.removeHint();

    this.snappingTimer = setTimeout(() => {
      this.renderer.removeStyle(el, 'transition');
      this.renderer.removeStyle(el, 'transform');
      this.snappingTimer = null;
    }, 200);
  }

  private updateHint(): void {
    if (this.swipeOffset > 0 && !this.hintElement) {
      this.createHint();
    }

    if (this.hintElement) {
      const opacity: number = Math.min(this.swipeOffset / this.swipeThreshold, 1);
      this.renderer.setStyle(this.hintElement, 'opacity', `${opacity}`);
    }
  }

  private createHint(): void {
    const host: HTMLElement = this.elementRef.nativeElement;
    const isLeft: boolean = this.libSwipeReplyDirection() === 'left';

    this.hintElement = this.renderer.createElement('div');

    const hintStyles: Record<string, string> = {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '28px',
      height: '28px',
      'border-radius': '50%',
      'background-color': 'rgba(90, 49, 244, 0.15)',
      display: 'flex',
      'align-items': 'center',
      'justify-content': 'center',
      'pointer-events': 'none',
      opacity: '0'
    };

    if (isLeft) {
      hintStyles['right'] = '-36px';
    } else {
      hintStyles['left'] = '-36px';
    }

    Object.entries(hintStyles).forEach(([key, value]: [string, string]) => {
      this.renderer.setStyle(this.hintElement, key, value);
    });

    const icon: HTMLElement = this.renderer.createElement('ion-icon');
    this.renderer.setAttribute(icon, 'name', 'arrow-undo');
    this.renderer.setStyle(icon, 'font-size', '14px');
    this.renderer.setStyle(icon, 'color', '#5a31f4');
    this.renderer.appendChild(this.hintElement, icon);

    this.renderer.appendChild(host, this.hintElement);
  }

  private removeHint(): void {
    if (this.hintElement && this.hintElement.parentElement) {
      this.renderer.removeChild(this.hintElement.parentElement, this.hintElement);
      this.hintElement = null;
    }
  }
}
